/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import { IconButton, Box, Tooltip } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { StyledHeaderCellTypo, StyledBodyCellTypo } from "../Users.jsx";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import AcceptNewUserModal from "../modals/AcceptNewUserModal.jsx";
import { useState } from "react";
import PropTypes from "prop-types";
import RejectUserModal from "../modals/RejectUserModal.jsx";
import UserDetailsModal from "../modals/UserDetailsModal.jsx";

const SORT_ICON_STYLE = {
  color: "#12141599",
  height: "16px",
  width: "16px",
};

const SortArrowIcon = ({ isActive, isAscending, isHovered }) => {
  if (isActive) {
    if (isAscending) {
      return <ArrowUpwardIcon sx={{ ...SORT_ICON_STYLE, transition: "color 0.2s ease" }} />;
    }
    return <ArrowDownwardIcon sx={{ ...SORT_ICON_STYLE, transition: "color 0.2s ease" }} />;
  }

  if (isHovered) {
    return (
      <ArrowUpwardIcon
        sx={{ ...SORT_ICON_STYLE, opacity: 0.5, transition: "opacity 0.2s ease" }}
      />
    );
  }

  return null;
};

SortArrowIcon.propTypes = {
  isActive: PropTypes.bool.isRequired,
  isAscending: PropTypes.bool.isRequired,
  isHovered: PropTypes.bool.isRequired,
};

const RequestsTableData = ({
  searchTerm,
  currentTabStatus,
  requestsData,
}) => {
  const [openAcceptNewUserModal, setOpenAcceptNewUserModal] = useState(false);
  const [openRejectUserModal, setOpenRejectUserModal] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [openUserDetailsModal, setOpenUserDetailsModal] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const readOnly_GlobalAdmin =
    sessionStorage.getItem("readonly_GlobalAdmin") === "true";
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [sortBy, setSortBy] = useState("firstName");
  const [sortOrder, setSortOrder] = useState("asc");
  const [hoveredColumn, setHoveredColumn] = useState(null);

  const normalizeSortValue = (value) => {
    if (value === null || value === undefined) return "";
    return typeof value === "string" ? value.toLowerCase() : value;
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  const sortedData = [...(requestsData || [])].sort((a, b) => {
    const aValue = normalizeSortValue(a[sortBy]);
    const bValue = normalizeSortValue(b[sortBy]);
    let comparison = 0;
    if (aValue > bValue) {
      comparison = 1;
    } else if (aValue < bValue) {
      comparison = -1;
    }
    return sortOrder === "asc" ? comparison : -comparison;
  });

  const handleUserDetailsOpen = (id) => {
    const details = requestsData.find((user) => user.id === id);
    setUserDetails(details);
    setOpenUserDetailsModal(true);
  };

  const handleUserDetailsClose = () => {
    setOpenUserDetailsModal(false);
  };

  const handleRejectModalOpen = (index) => {
    setSelectedRowId(index);
    setOpenRejectUserModal(true);
  };

  const handleRejectModalClose = () => {
    setOpenRejectUserModal(false);
  };

  const handleAcceptModalOpen = (index) => {
    setSelectedRowId(index);
    const filteredData = requestsData.find((data) => data.id === index);
    setUserDetails(filteredData);
    setOpenAcceptNewUserModal(true);
  };

  const handleAcceptModalClose = () => {
    setOpenAcceptNewUserModal(false);
  };

  const renderSortableHeader = (title, fieldName) => (
    <Box
      sx={{ textAlign: "center", display: "flex", alignItems: "center" }}
      onMouseEnter={() => setHoveredColumn(fieldName)}
      onMouseLeave={() => setHoveredColumn(null)}
    >
      <StyledHeaderCellTypo>{title}</StyledHeaderCellTypo>
      <IconButton
        sx={{
          cursor: "pointer",
          borderRadius: "4px",
          marginLeft: "8px",
          width: "24px",
          height: "24px",
          padding: "4px",
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          },
        }}
        onClick={() => handleSort(fieldName)}
      >
        <SortArrowIcon
          isActive={sortBy === fieldName}
          isAscending={sortOrder === "asc"}
          isHovered={hoveredColumn === fieldName}
        />
      </IconButton>
    </Box>
  );

  const renderCellWithTooltip = (value, maxLength = 25, onClickHandler = null) => {
    const displayValue = value || "...";
    const truncated =
      displayValue.length > maxLength
        ? displayValue.substring(0, maxLength) + "..."
        : displayValue;
    const showTooltip = displayValue !== "...";

    return (
      <Box
        onClick={onClickHandler}
        sx={{
          cursor: onClickHandler ? "pointer" : "default",
          padding: "8px",
          width: "100%",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Tooltip
          title={showTooltip ? displayValue : ""}
          arrow
          placement="top"
          disableHoverListener={!showTooltip}
          slotProps={{
            tooltip: {
              sx: {
                bgcolor: "#121415F2",
                padding: "8px 12px 8px 12px",
                "& .MuiTooltip-arrow": {
                  color: "#121415F2",
                },
              },
            },
          }}
        >
          <StyledBodyCellTypo>{truncated}</StyledBodyCellTypo>
        </Tooltip>
      </Box>
    );
  };

  const columns = [
    {
      field: "registrationDate",
      headerName: "Date",
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderHeader: () => renderSortableHeader("Date", "registrationDate"),
      renderCell: (params) => (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            padding: "8px",
          }}
        >
          {renderCellWithTooltip(params.row.registrationDate ?? "...")}
        </Box>
      ),
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      minWidth: 200,
      sortable: false,
      renderHeader: () => renderSortableHeader("Email", "email"),
      renderCell: (params) =>
        renderCellWithTooltip(
          params.row.email || "...",
          25,
          () => handleUserDetailsOpen(params.row.id)
        ),
    },
    {
      field: "firstName",
      headerName: "Publisher",
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderHeader: () => renderSortableHeader("Publisher", "firstName"),
      renderCell: (params) =>
        renderCellWithTooltip(params.row.firstName || "...", 20),
    },
    {
      field: "role",
      headerName: "Role",
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderHeader: () => renderSortableHeader("Role", "portal"),
      renderCell: (params) =>
        renderCellWithTooltip(params.row?.portal || "...", 20),
    },
    {
      field: "accept",
      headerName: "Accept",
      width: 80,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: () => (
        <Box sx={{ textAlign: "center" }}>
          <StyledHeaderCellTypo>Accept</StyledHeaderCellTypo>
        </Box>
      ),
      renderCell: (params) => (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "8px",
          }}
        >
          <IconButton
            onClick={() => handleAcceptModalOpen(params.row.id)}
            disabled={readOnly_GlobalAdmin}
          >
            <CheckIcon sx={{ color: "#0476E2" }} />
          </IconButton>
        </Box>
      ),
    },
    {
      field: "reject",
      headerName: "Reject",
      width: 80,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: () => (
        <Box sx={{ textAlign: "center" }}>
          <StyledHeaderCellTypo>Reject</StyledHeaderCellTypo>
        </Box>
      ),
      renderCell: (params) => (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "8px",
          }}
        >
          <IconButton
            onClick={() => handleRejectModalOpen(params.row.id)}
            disabled={readOnly_GlobalAdmin}
          >
            <CloseIcon sx={{ color: "#B50B00" }} />
          </IconButton>
        </Box>
      ),
    },
  ];

  const dataGridStyles = {
    "& .MuiDataGrid-columnHeader": {
      fontSize: "12px",
      fontWeight: "600",
      backgroundColor: "#E8E8E8 !important",
      color: "#121415",
      lineHeight: "30px",
      padding: "0px 10px",
      display: "flex",
      alignItems: "center",
      borderBottom: "1px solid rgba(18, 20, 21, 0.2)",
    },
    "& .MuiDataGrid-columnHeaderTitle": {
      fontSize: "12px",
      fontWeight: "bold",
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
      minHeight: "30px",
      lineHeight: "30px",
      padding: "16px",
    },
    "& .MuiDataGrid-cell": {
      borderRight: "1px solid rgba(18, 20, 21, 0.2)",
      borderBottom: "none !important",
      fontSize: "12px",
      lineHeight: "20px",
      padding: "0px",
      display: "flex",
      alignItems: "center",
    },
    "& .MuiDataGrid-cell:last-child": { borderRight: "none" },
    "& .MuiDataGrid-container--top [role=row]": {
      background: "#E8E8E8 !important",
    },
    "& .MuiDataGrid-virtualScroller": {
      marginTop: "0px !important",
    },
    "& .MuiDataGrid-virtualScrollerContent": {
      overflowX: "hidden !important",
    },
    "& .MuiDataGrid-row": {
      height: "56px",
      borderBottom: "1px solid rgba(18, 20, 21, 0.2)",
      "&:hover": {
        backgroundColor: "rgba(0, 0, 0, 0.04)",
      },
    },
  };

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <DataGrid
          rows={sortedData || []}
          columns={columns}
          getRowId={(row) => row.id}
          pagination
          pageSizeOptions={[5, 10, 15]}
          paginationMode="client"
          disableColumnMenu
          disableRowSelectionOnClick
          loading={false}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setPageSize(model.pageSize);
          }}
          localeText={{
            MuiTablePagination: {
              labelRowsPerPage: "Items per page:",
            },
          }}
          sx={dataGridStyles}
        />
      </Box>

      <UserDetailsModal
        openUserDetailsModal={openUserDetailsModal}
        handleUserDetailsClose={handleUserDetailsClose}
        userDetails={userDetails}
        searchTerm={searchTerm}
      />

      <AcceptNewUserModal
        userDetails={userDetails}
        openAcceptNewUserModal={openAcceptNewUserModal}
        handleAcceptUserClose={handleAcceptModalClose}
        searchTerm={searchTerm}
      />

      <RejectUserModal
        openRejectUserModal={openRejectUserModal}
        handleRejectUserClose={handleRejectModalClose}
        searchTerm={searchTerm}
        selectedRowId={selectedRowId}
      />
    </>
  );
};

RequestsTableData.propTypes = {
  searchTerm: PropTypes.string.isRequired,
  currentTabStatus: PropTypes.string.isRequired,
  requestsData: PropTypes.array.isRequired,
};

export default RequestsTableData;
